import Box from '@mui/material/Box'
import { styled } from '@mui/system'
import type { Theme } from '@mui/system'
import { grey } from '../../colors'
import { TextField } from '@mui/material'
import FormLabel from '@mui/material/FormLabel'

export const StyledBox = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(10),
  marginTop: theme.spacing(15)
}))

export const SectionList = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2)
}))

export const StyledQuestionBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.borderColor as string}`,
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  width: '100%',
  transition: '0.5s'
}))

export const StyledCreateQuestionBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.borderColor as string}`,
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  width: '100%',
  transition: '0.5s',
  boxShadow: ' 0 0 0 2px rgba(66, 133, 244, 0.6)'
}))

export const StyledQuestionBoxHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  backgroundColor: grey[100],
  borderBottom: `1px solid ${theme.palette.borderColor as string}`,
  padding: `${theme.spacing(2.25)} ${theme.spacing(0)} ${theme.spacing(2.25)} ${theme.spacing(4.5)}`
}))

export const SectionWrapper = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.borderColor as string}`,
  borderRadius: theme.spacing(3),
  width: '100%',
  transition: '0.5s',
  padding: theme.spacing(6),
  position: 'relative'
}))

export const SectionRow = styled(Box)(({ theme }) => ({
  width: '73%',
  display: 'flex',
  gap: theme.spacing(3.5)
}))

interface SectionConnectorProps {
  connectorColor: string
  connectTop?: boolean
  connectBottom?: boolean
}

const shouldForwardSectionConnectorProp = (prop: PropertyKey): boolean =>
  !['connectorColor', 'connectTop', 'connectBottom'].includes(String(prop))

// Component to render the vertical connector line between sections in the same branching group,
// it receives props to determine if it should connect to the top or bottom section,
// and the color of the connector line
export const SectionConnector = styled(Box, {
  shouldForwardProp: shouldForwardSectionConnectorProp
})<SectionConnectorProps>(({ theme, connectorColor, connectTop, connectBottom }) => ({
  position: 'relative',
  width: '12px',
  minWidth: '12px',
  alignSelf: 'stretch',
  backgroundColor: connectorColor,
  borderTopLeftRadius: connectTop ? 0 : theme.spacing(2),
  borderTopRightRadius: connectTop ? 0 : theme.spacing(2),
  borderBottomLeftRadius: connectBottom ? 0 : theme.spacing(2),
  borderBottomRightRadius: connectBottom ? 0 : theme.spacing(2),
  '&::before': connectTop
    ? {
        content: '""',
        position: 'absolute',
        top: theme.spacing(-2),
        left: 0,
        right: 0,
        height: theme.spacing(2),
        backgroundColor: connectorColor
      }
    : {},
  '&::after': connectBottom
    ? {
        content: '""',
        position: 'absolute',
        bottom: theme.spacing(-2),
        left: 0,
        right: 0,
        height: theme.spacing(2),
        backgroundColor: connectorColor
      }
    : {}
}))

export const StyledTextField = styled(TextField)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  width: '73%',
  border: `1px solid ${theme.palette.borderColor as string}`,
  '& input': {
    fontSize: '20px',
    fontWeight: 600,
    textAlign: 'center'
  },
  '& input::placeholder': {
    opacity: 1,
    color: grey[800],
    fontWeight: '500'
  },
  '& fieldset': {
    border: 'none'
  }
}))

export const PreparedEmptyField = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2.5),
  height: theme.spacing(11),
  border: '1px solid rgba(0, 0, 0, 0.23)',
  borderRadius: theme.spacing(1.5),
  color: '#D3D8DB'
}))

export const StyledFormLabel = styled(FormLabel)(({ theme }: { theme: Theme }) => ({
  mb: theme.spacing(5),
  fontWeight: '500',
  letterSpacing: '0.01em',
  whiteSpace: 'unset'
}))
